import axios, { Axios } from "axios";

import { deleteCookie } from "../utility/utils";

const axiosInstance = (paramsObject: any): Axios => {
    const { props } = paramsObject;
    const {
        builderPreview,
        builder,
        templatePreview,
        componentsPreview,
        nextJs,
    } = props;

    const instance = axios.create();

    if (nextJs) {
        /**
         * All requests go through the Next.js proxy.
         *
         * Browser
         *    ↓
         * /api/proxy/*
         *    ↓
         * userapiserver
         */
        instance.defaults.baseURL = "/api/proxy";
    } else {
        instance.defaults.baseURL = process.env.REACT_APP_USERS_API_SERVER_URL;
    }

    instance.defaults.headers.get["Accept"] = "application/json";
    instance.defaults.headers.post["Accept"] = "application/json";

    /**
     * Request Source
     */
    instance.defaults.headers.common["x-request-origin"] =
        builder || builderPreview || componentsPreview
            ? "builder"
            : templatePreview
                ? "templatePreview"
                : "user_site";

    /**
     * Allow browser to include cookies.
     */
    instance.defaults.withCredentials = true;

    /**
     * Authorization Header
     *
     * 1. Next.js Website
     *    Uses the project token received from the server component.
     *
     * 2. Builder / Builder Preview / Template Preview / Components Preview
     *    Uses the project token stored in sessionStorage.
     *
     * 3. Standalone React Website
     *    Uses the public access token defined in environment variables.
     */
    let authorizationToken: string;

    if (nextJs) {
        authorizationToken = props.token ?? "";

    } else if (
        builder ||
        builderPreview ||
        templatePreview ||
        componentsPreview
    ) {
        authorizationToken = sessionStorage.getItem("token") ?? "";

    } else {
        authorizationToken = process.env.REACT_APP_ACCESS_TOKEN ?? "";
    }

    if (authorizationToken) {
        instance.defaults.headers.common.Authorization = `Bearer ${authorizationToken}`;
    }

    /**
     * Request Interceptor
     */
    instance.interceptors.request.use(
        (request) => request,
        (error) => {
            alert("Unable to connect to the server.");
            return Promise.reject(error);
        }
    );

    /**
     * Response Interceptor
     */
    instance.interceptors.response.use(
        (response) => response,

        async (error) => {

            if (
                error.response &&
                paramsObject.data.authenticated &&
                error.response.status === 401
            ) {

                try {
                    // Website running on Next.js
                    if (nextJs) {
                        // Remove Login Cookie
                        await deleteCookie("userToken");
    
                        // Remove Password Reset Cookie
                        await deleteCookie("passwordResetToken");
                    }

                    // Builder / Template Preview
                    else if (
                        builder ||
                        builderPreview ||
                        templatePreview ||
                        componentsPreview
                    ) {
                        sessionStorage.removeItem("token");
                    }

                } catch (e) {
                    console.error("Unable to clear authentication cookies.", e);
                }

                alert("Session expired, please login again");

                window.location.href = "/login";

                return Promise.reject(
                    new Error("Session expired, please login again")
                );
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

export default axiosInstance;